import { sendPostMessage } from 'helpers/post-message.helper';
import { useContext, useState } from 'preact/hooks';
import { wait } from 'helpers/operators';
import { ApplicationContext } from 'contexts/use-application-setup';
import { PDFReportBranding, PDFReportResult } from 'constants/violations';
import { useApi } from './use-api';

declare const applicationConfig: { base: { api: string } };

const TARGET_PAGE_DOWNLOAD_TIME = 1000;
const REPORT_RESULT_TIMEOUT = 3000;

const getPdfUrl = (siteUrl: string) =>
  `${applicationConfig.base.api}a11y-data/v0/site/${siteUrl}/acc-report-pdf`;

export const useViolationsReport = () => {
  const Api = useApi();
  const { config, siteUrl } = useContext(ApplicationContext);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [reportResult, setReportResult] = useState<PDFReportResult | null>(null);

  const downloadPdfPage = (pdfFileUrl: string) => {
    const url = getPdfUrl(siteUrl);

    sendPostMessage({
      action: 'download_pdf',
      url: `${url}/${pdfFileUrl}`,
    });

    // We don't know how long PDF report file will be downloaded by the target page
    return wait(TARGET_PAGE_DOWNLOAD_TIME);
  };

  const getAccessibilityPDFReportBranding = (whiteLabelled = false) => {
    if (whiteLabelled) {
      return PDFReportBranding.whitelabel;
    }

    const isShortpointEnv = config.current.services?.partner === PDFReportBranding.shortpoint;
    if (isShortpointEnv) {
      return PDFReportBranding.shortpoint;
    }

    return PDFReportBranding.userway;
  };

  const downloadPdfReport = async (whiteLabelledReport = false) => {
    if (isReportLoading) {
      return;
    }

    try {
      setIsReportLoading(true);

      const branding = getAccessibilityPDFReportBranding(whiteLabelledReport);
      const url = getPdfUrl(siteUrl);

      const response = await Api.get(`${url}?branding=${branding}`, {
        uwAiWidgetState: 'WIDGET_OFF',
      });

      const pdfFileUrl = response?.payload?.accReportPdfName;
      if (!pdfFileUrl) {
        throw new Error('No PDF Report found!');
      }

      downloadPdfPage(pdfFileUrl);
      setReportResult(PDFReportResult.success);
    } catch (error) {
      console.error('PDF Report Error:', error);
      setReportResult(PDFReportResult.error);
    } finally {
      setIsReportLoading(false);

      setTimeout(() => {
        setReportResult(null);
      }, REPORT_RESULT_TIMEOUT);
    }
  };

  return {
    downloadPdfReport,
    isReportLoading,
    reportResult,
  };
};
